import {
  buildBatchApiPaths,
  GET_EARLIEST_DELIVERY_DATE_URL,
  GET_PLP_EXPERIMENT_RESOLVE_URL,
  GET_PLP_SERVICEABILITY_URL,
  GET_CMS_BATCH_API_URL,
  GET_PLP_PRODUCT_FILTER_FOR_MOBILE,
  GET_PRODUCT_ATTR_TAG_URL,
  GET_PDP_WIDGET_URL,
  GET_PRODUCT_LISTING_URL,
  PDP_WIDGET_TYPES,
  GET_WIDGET_PRODUCTS_URL,
  mapBatchApiResponse,
  PLP_CMS_BATCH_SERVER_CACHE_TTL_MS,
  PLP_HEAD_ANALYTICS,
} from "./constants";
import { InterceptorClient } from "../../interceptor/interceptorClient";
import { withServerCache } from "../../utils/cache-layer/serverCache";
import { isInterceptorClientErrorShape, isLikelyCmsBatchPayload } from "../../utils/cmsBatch";
const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
const searchServerAPIUrl = process.env.NEXT_PUBLIC_SEARCH_SERVER_API_GATEWAY;
const r2ApiGatewayUrl = process.env.NEXT_PUBLIC_R2_CLIENT_API_GATEWAY;
const isServer = typeof window === "undefined";

const basicHeaders = {
  "Access-Control-Allow-Credentials": "true",
  "Access-Control-Allow-Headers": "Content-Type",
  "Content-Type": "text/html;charset=UTF-8",
  "X-made-with": "Made with <3 in India",
};

/**
 * Fetches product listing details from the API.
 * @returns {Promise<unknown>} A promise that resolves to the product listing details.
 */
export const getProductListingDetailsAPI = async (params, isMobile, headersList) => {
  const baseUrl = isServer ? searchServerAPIUrl : "";

  return await InterceptorClient.getGatewayInstance(baseUrl).get(`${GET_PRODUCT_LISTING_URL(params)}`, false, null, {
    ...basicHeaders,
    "x-device-type": isMobile ? "mobile" : "desktop",
    "x-domain": "www.fnp.com",
    "edge-control": headersList?.["edge-control"],
    "edge-cache-tag": headersList?.["edge-cache-tag"],
    expires: headersList?.expires || headersList?.Expires,
  });
};
/**
 * Resolves the experiment bucket (campaign, segment, group) for a PLP category.
 *
 * Browser-only companion to the listing API: the listing response ships the same targeting fields,
 * but it is CDN-cached, so those fields can belong to a different user.
 *
 * @param {Object} params
 * @param {string} params.categoryUrl - Category url, e.g. `fnp.com/gifts/daughters-day-lp`.
 * @param {string} [params.geoId] - Catalog id.
 * @param {string} [params.email] - Logged-in user email.
 * @param {string} [params.fnpId] - Anonymous experiment id (`ab_user_id`).
 * @returns {Promise<Object>} Resolve payload, or the interceptor error shape when the call fails.
 */
export const getPlpExperimentResolveAPI = async ({ categoryUrl, geoId, email, fnpId }) => {
  try {
    return await InterceptorClient.getGatewayInstance().get(
      `${GET_PLP_EXPERIMENT_RESOLVE_URL({ categoryUrl, geoId, email, fnpId })}`,
    );
  } catch (error) {
    console.log("Error resolving PLP experiment", error);
    return {};
  }
};

/**
 * Fetches product listing details from the API.
 * @returns {Promise<unknown>} A promise that resolves to the product listing details.
 */
export const getProductListingFilterForMobile = async (params) => {
  return await InterceptorClient.getGatewayInstance().get(`${GET_PLP_PRODUCT_FILTER_FOR_MOBILE(params)}`);
};

/**
 * Legacy earliest delivery API — used by PDP ChooseDeliveryType only.
 *
 * @param {string} productIDs - Comma-separated product IDs
 * @param {string} pincode
 * @returns {Promise<unknown>}
 */
export const getDeliveryDatesAPI = async (productIDs, pincode) => {
  const res = await InterceptorClient.getFNTSInstance(baseUrl).get(
    `${GET_EARLIEST_DELIVERY_DATE_URL(productIDs, pincode)}`,
  );
  return res;
};

/**
 * Fetches PLP serviceability delivery dates and display tags.
 *
 * @param {string} productIDs - Comma-separated product IDs
 * @param {string} pincode
 * @param {Object} [options]
 * @param {number|string} [options.lat]
 * @param {number|string} [options.long]
 * @returns {Promise<{ deliveryDates: Object, message: string, isDatesAvailable: boolean }>}
 */
export const getPlpServiceabilityAPI = async (productIDs, pincode, { lat, long } = {}) => {
  return await InterceptorClient.getGatewayInstance(r2ApiGatewayUrl).get(
    `${GET_PLP_SERVICEABILITY_URL({ pincode, pids: productIDs, lat, long })}`,
  );
};

/**
 *
 * @param {*}
 * @returns
 */
export const getProductsAttrTag = async () => {
  try {
    const response = await InterceptorClient.getGatewayInstance().get(`${GET_PRODUCT_ATTR_TAG_URL()}`);
    if (!response.status) {
      return response;
    } else {
      return {};
    }
  } catch (error) {
    return {};
  }
};

/**
 * Normalizes product IDs to a non-empty array for the products-by-ids API.
 *
 * @param {string|string[]|number[]} productIds
 * @returns {string[]}
 */
export const normalizeProductIds = (productIds) => {
  if (!productIds) {
    return [];
  }

  if (Array.isArray(productIds)) {
    return productIds.map(String).filter(Boolean);
  }

  if (typeof productIds === "string") {
    return productIds
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
  }

  return [];
};

/**
 * Extracts the product array from a products-by-ids API response.
 *
 * @param {Object} response
 * @returns {Array<Object>}
 */
export const getProductsFromByIdsResponse = (response) => {
  return Array.isArray(response?.data) ? response.data : [];
};

/**
 * API for the data for Gift Finder
 * @returns
 */
export const getProductsByIdsData = async (productIds) => {
  const ids = normalizeProductIds(productIds);

  if (ids.length === 0) {
    return { data: [] };
  }

  return await InterceptorClient.getGatewayInstance().get(`${GET_WIDGET_PRODUCTS_URL(ids)}`);
};

/**
 * Unified PDP widget API (alternatives, CVO, trending) — shared response schema.
 *
 * @param {Object} params
 * @param {string} params.widgetType - alternatives | cvo | trending
 * @param {string} params.productId
 * @param {string} [params.pincode]
 * @param {string} [params.geo]
 * @param {number} [params.page]
 * @param {number} [params.size]
 * @returns {Promise<Object>}
 */
export const getPdpWidgetAPI = async ({ widgetType, productId, pincode, geo, page, size }) => {
  return await InterceptorClient.getGatewayInstance().get(
    `${GET_PDP_WIDGET_URL({ widgetType, productId, pincode, geo, page, size })}`,
  );
};

export { PDP_WIDGET_TYPES };

/**
 * Fetches analytics data from the analytics data API.
 *
 * @returns {Promise<Object>} Returns a promise that resolves to the response
 *                            from the analytics data API.
 */
export const plpHeadAnalytics = async (isMobile) => {
  return await InterceptorClient.getGatewayInstance().get(`${PLP_HEAD_ANALYTICS()}`, false, null, {
    "x-device-type": isMobile ? "mobile" : "desktop",
    "x-domain": "www.fnp.com",
  });
};

/**
 * Fetches batch CMS data for PLP page via CMS server gateway (`NEXT_PUBLIC_CMS_SERVER_API_GATEWAY`).
 * Caching is handled by the caller (`getCmsBatchMapped`) to avoid duplicate cache entries.
 *
 * @param {string} categoryId - PLP category id for the batch request path segment
 * @param {boolean} isMobile - Whether the request is from mobile
 * @param {string} [catalogId=""] - The catalog ID for menu path
 * @returns {Promise<Object>} Batch API response map keyed by CMS path, or `{}` on failure
 */
export const getCmsBatchApi = async (categoryId, isMobile, catalogId = "") => {
  try {
    const pathsArray = buildBatchApiPaths({ isMobile, catalogId });
    const relativeUrl = GET_CMS_BATCH_API_URL(categoryId, pathsArray);
    const response = await InterceptorClient.getServerCMSInstance().get(relativeUrl, false, null, {
      ...basicHeaders,
      "x-device-type": isMobile ? "mobile" : "desktop",
      "x-domain": "www.fnp.com",
    });

    if (isInterceptorClientErrorShape(response)) {
      throw new Error(`PLP batch HTTP ${response.status}`);
    }
    if (!isLikelyCmsBatchPayload(response)) {
      return {};
    }
    return response;
  } catch (error) {
    console.log("Error fetching batch API", error);
    return {};
  }
};

/**
 * Fetches PLP batch CMS data and returns the mapped + JSON-parsed bundle, cached as a single
 * unit so cache hits skip re-mapping and re-parsing on every render.
 *
 * @param {Object} args
 * @param {string} args.categoryId
 * @param {boolean} args.isMobile
 * @param {string} [args.catalogId]
 * @param {string} [args.categoryIdForReviews]
 * @returns {Promise<Object>} Mapped batch response with the `parsed` bundle
 */
export const getCmsBatchMapped = async ({ categoryId, isMobile, catalogId = "", categoryIdForReviews = "" }) => {
  const cacheKey = `plp:${categoryId}:${isMobile ? "m" : "d"}:${catalogId || "-"}`;
  try {
    return await withServerCache(
      cacheKey,
      async () => {
        const raw = await getCmsBatchApi(categoryId, isMobile, catalogId);
        return mapBatchApiResponse(raw, { isMobile, categoryId, categoryIdForReviews });
      },
      PLP_CMS_BATCH_SERVER_CACHE_TTL_MS,
    );
  } catch (error) {
    console.log("Error fetching mapped batch", error);
    return {
      plpHeadAnalyticsResponse: {},
      metaTagsResponse: {},
      productReviews: {},
      appRedirectBannerResponse: {},
      freeBannerData: null,
      parsed: {
        footerLinks: {},
        scripts: [],
        preLoadArray: [],
        languages: {},
        metaContent: { title: "", description: "", keywords: "" },
        canonicalUrl: "",
        twitterTags: null,
      },
    };
  }
};
